/**
 * Session Manager - Prevents data mixing between different registration flows
 * This utility adds protection without disrupting existing functionality
 */

// Define flow types
export const FLOW_TYPES = {
  ETHIOPIAN_REGISTRATION: 'ethiopian_registration',
  NON_ETHIOPIAN_REGISTRATION: 'non_ethiopian_registration', 
  EXISTING_STUDENT_LOGIN: 'existing_student_login',
  ADMIN_LOGIN: 'admin_login',
  PROFILE_VIEW: 'profile_view'
};

// Define localStorage keys for each flow
const FLOW_KEYS = {
  [FLOW_TYPES.ETHIOPIAN_REGISTRATION]: [
    'fanNumber',
    'fayda_code_verifier', 
    'fayda_state',
    'fayda_timestamp',
    'userData'
  ],
  [FLOW_TYPES.NON_ETHIOPIAN_REGISTRATION]: [
    'userData'
  ],
  [FLOW_TYPES.EXISTING_STUDENT_LOGIN]: [
    'exit_username',
    'authToken',
    'userData'
  ],
  [FLOW_TYPES.ADMIN_LOGIN]: [
    'authToken',
    'token',
    'userData'
  ],
  [FLOW_TYPES.PROFILE_VIEW]: [
    'token',
    'authToken', 
    'userData'
  ]
};

/**
 * Detect which flow is currently active based on localStorage data
 * @returns {string|null} Active flow type or null if no clear flow detected
 */
export const detectActiveFlow = () => {
  console.log('🔍 Detecting active flow...');
  
  // Check for Ethiopian registration flow
  const fanNumber = localStorage.getItem('fanNumber');
  const faydaCodeVerifier = localStorage.getItem('fayda_code_verifier');
  const faydaState = localStorage.getItem('fayda_state');
  
  if (fanNumber || faydaCodeVerifier || faydaState) {
    console.log('✅ Ethiopian registration flow detected');
    return FLOW_TYPES.ETHIOPIAN_REGISTRATION;
  }
  
  // Check for existing student login flow
  const exitUsername = localStorage.getItem('exit_username');
  const authToken = localStorage.getItem('authToken');
  
  if (exitUsername && authToken) {
    console.log('✅ Existing student login flow detected');
    return FLOW_TYPES.EXISTING_STUDENT_LOGIN;
  }
  
  // Check for admin login flow
  const token = localStorage.getItem('token');
  if (token && !exitUsername) {
    console.log('✅ Admin login flow detected');
    return FLOW_TYPES.ADMIN_LOGIN;
  }
  
  // Check for non-Ethiopian registration (has userData but no Ethiopian-specific keys)
  const userData = localStorage.getItem('userData');
  if (userData && !fanNumber && !exitUsername) {
    console.log('✅ Non-Ethiopian registration flow detected');
    return FLOW_TYPES.NON_ETHIOPIAN_REGISTRATION;
  }
  
  console.log('❌ No clear flow detected');
  return null;
};

/**
 * Clean up conflicting data when switching flows
 * @param {string} targetFlow - The flow we're switching to
 * @param {boolean} preserveUserData - Whether to preserve userData (default: true)
 */
export const cleanupConflictingData = (targetFlow, preserveUserData = true) => {
  console.log(`🧹 Cleaning up conflicting data for flow: ${targetFlow}`);
  
  const currentFlow = detectActiveFlow();
  
  // If no current flow or same flow, no cleanup needed
  if (!currentFlow || currentFlow === targetFlow) {
    console.log('✅ No cleanup needed');
    return;
  }
  
  console.log(`🔄 Switching from ${currentFlow} to ${targetFlow}`);
  
  // Get keys to clean up (all keys from other flows)
  const keysToClean = [];
  Object.entries(FLOW_KEYS).forEach(([flow, keys]) => {
    if (flow !== targetFlow) {
      keysToClean.push(...keys);
    }
  });
  
  // Remove duplicates
  const uniqueKeysToClean = [...new Set(keysToClean)];
  
  // Clean up keys, but preserve userData if requested
  uniqueKeysToClean.forEach(key => {
    if (key === 'userData' && preserveUserData) {
      console.log(`⏭️ Preserving userData for flow transition`);
      return;
    }
    
    if (localStorage.getItem(key)) {
      console.log(`🗑️ Removing conflicting key: ${key}`);
      localStorage.removeItem(key);
    }
  });
  
  console.log('✅ Cleanup completed');
};

/**
 * Validate data consistency for the current flow
 * @param {string} flowType - The flow type to validate
 * @returns {object} Validation result with isValid and issues
 */
export const validateFlowData = (flowType) => {
  console.log(`🔍 Validating data for flow: ${flowType}`);
  
  const requiredKeys = FLOW_KEYS[flowType] || [];
  const issues = [];
  
  // Check for required keys
  requiredKeys.forEach(key => {
    if (!localStorage.getItem(key)) {
      issues.push(`Missing required key: ${key}`);
    }
  });
  
  // Check for conflicting keys from other flows
  Object.entries(FLOW_KEYS).forEach(([flow, keys]) => {
    if (flow !== flowType) {
      keys.forEach(key => {
        if (localStorage.getItem(key)) {
          issues.push(`Conflicting key from ${flow}: ${key}`);
        }
      });
    }
  });
  
  const isValid = issues.length === 0;
  
  console.log(`✅ Validation result: ${isValid ? 'VALID' : 'INVALID'}`);
  if (issues.length > 0) {
    console.log('❌ Issues found:', issues);
  }
  
  return { isValid, issues };
};

/**
 * Initialize a flow with proper cleanup
 * @param {string} flowType - The flow type to initialize
 * @param {boolean} forceCleanup - Force cleanup even if same flow (default: false)
 */
export const initializeFlow = (flowType, forceCleanup = false) => {
  console.log(`🚀 Initializing flow: ${flowType}`);
  
  const currentFlow = detectActiveFlow();
  
  // Clean up if switching flows or if forced
  if (currentFlow !== flowType || forceCleanup) {
    cleanupConflictingData(flowType);
  }
  
  // Validate the flow data
  const validation = validateFlowData(flowType);
  
  if (!validation.isValid) {
    console.warn('⚠️ Flow validation failed:', validation.issues);
    // Don't throw error - just log warning to avoid disrupting existing flows
  }
  
  console.log(`✅ Flow ${flowType} initialized successfully`);
  return validation;
};

/**
 * Get current session info for debugging
 * @returns {object} Current session information
 */
export const getSessionInfo = () => {
  const activeFlow = detectActiveFlow();
  const allKeys = Object.values(FLOW_KEYS).flat();
  const presentKeys = allKeys.filter(key => localStorage.getItem(key));
  
  return {
    activeFlow,
    presentKeys,
    timestamp: new Date().toISOString()
  };
};

/**
 * Emergency cleanup - clears all session data
 * Use only when absolutely necessary
 */
export const emergencyCleanup = () => {
  console.log('🚨 Emergency cleanup - clearing all session data');
  
  const allKeys = Object.values(FLOW_KEYS).flat();
  const uniqueKeys = [...new Set(allKeys)];
  
  uniqueKeys.forEach(key => {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
      console.log(`🗑️ Removed: ${key}`);
    }
  });
  
  console.log('✅ Emergency cleanup completed');
};
